import React from "react";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * Global loading theme for PLUTO. Every page uses these primitives so
 * the “flash of loading” across the app feels consistent — soft purple
 * pulse blocks (shadcn Skeleton) instead of grey text.
 *
 * Building blocks:
 *   • <PageSkeleton>          — top-level page loader (title + cards + rows).
 *   • <ScopeViewSkeleton>     — full-width scope dashboard loader.
 *   • <ProjectViewSkeleton>   — full-width project spreadsheet loader.
 *   • <TableRowsSkeleton>     — one-liner replacement inside `<tbody>`.
 *   • <InlineSkeleton>        — small inline pulse bar (e.g. sidebar).
 *   • <CardBlockSkeleton>     — a grid of card-shaped placeholders.
 */

// ─────────────────────────────────────────────────────────────
// SkeletonShimmerWrap — internal helper. Plain container.
// (The diagonal sweep flash was removed per user request; the base
// `animate-pulse` on individual Skeleton blocks is the only motion.)
// ─────────────────────────────────────────────────────────────
function SkeletonShimmerWrap({ className = "", children, ...rest }) {
  return (
    <div className={className} {...rest}>
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// PageSkeleton — used when the entire page is still loading.
// ─────────────────────────────────────────────────────────────
export function PageSkeleton({
  title = true,
  cards = 0,
  rows = 6,
  filters = false,
  className = "",
}) {
  return (
    <SkeletonShimmerWrap
      className={`max-w-[1600px] mx-auto p-6 space-y-5 ${className}`}
      data-testid="page-skeleton"
    >
      {title && (
        <div className="space-y-2">
          <Skeleton className="h-7 w-56" />
          <Skeleton className="h-3 w-72" />
        </div>
      )}
      {cards > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Array.from({ length: cards }).map((_, i) => (
            <div
              key={i}
              className="rounded-lg border border-slate-200 bg-white p-3"
            >
              <Skeleton className="h-3 w-16" />
              <Skeleton className="h-7 w-14 mt-2" />
              <Skeleton className="h-2.5 w-20 mt-2" />
            </div>
          ))}
        </div>
      )}
      {filters && (
        <div className="flex items-center gap-2 flex-wrap bg-white border border-slate-200 rounded-lg p-3">
          <Skeleton className="h-9 w-40" />
          <Skeleton className="h-9 w-56" />
          <Skeleton className="h-9 w-40" />
          <Skeleton className="h-9 w-40" />
        </div>
      )}
      <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="flex items-center gap-3">
            <Skeleton className="h-8 w-8 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-3 w-56" />
              <Skeleton className="h-2.5 w-40" />
            </div>
            <Skeleton className="h-4 w-14" />
          </div>
        ))}
      </div>
    </SkeletonShimmerWrap>
  );
}

// ─────────────────────────────────────────────────────────────
// TableRowsSkeleton — drop into any <tbody> where rows are loading.
// ─────────────────────────────────────────────────────────────
export function TableRowsSkeleton({ cols = 6, rows = 5, firstAvatar = false }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, r) => (
        <tr key={r} data-testid="table-row-skeleton">
          {Array.from({ length: cols }).map((_, c) => (
            <td key={c} className="px-3 py-3">
              {c === 0 && firstAvatar ? (
                <div className="flex items-center gap-2">
                  <Skeleton className="h-7 w-7 rounded-full" />
                  <div className="flex-1">
                    <Skeleton className="h-3 w-28" />
                    <Skeleton className="h-2.5 w-36 mt-1.5" />
                  </div>
                </div>
              ) : (
                <Skeleton
                  className={
                    c === 0 ? "h-3.5 w-32" : c % 2 === 0 ? "h-3 w-16" : "h-3 w-24"
                  }
                />
              )}
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// InlineSkeleton — replaces small “Loading…” text bits.
// ─────────────────────────────────────────────────────────────
export function InlineSkeleton({ className = "", lines = 1, width = "w-40" }) {
  if (lines === 1) {
    return (
      <Skeleton
        className={`h-3 ${width} ${className}`}
        data-testid="inline-skeleton"
      />
    );
  }
  return (
    <div className={`space-y-1.5 ${className}`} data-testid="inline-skeleton">
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} className={`h-3 ${i === lines - 1 ? "w-3/5" : width}`} />
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// CardBlockSkeleton — n card-shaped blocks (used for HiringRequests,
// dashboard tiles, etc).
// ─────────────────────────────────────────────────────────────
export function CardBlockSkeleton({ count = 3, className = "" }) {
  return (
    <div className={`space-y-3 ${className}`} data-testid="card-block-skeleton">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="rounded-lg border border-slate-200 bg-white p-4 flex items-center gap-3"
        >
          <Skeleton className="h-10 w-10 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-3.5 w-48" />
            <Skeleton className="h-2.5 w-72" />
          </div>
          <Skeleton className="h-6 w-16 rounded-full" />
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// ScopeViewSkeleton — full-width loader that mirrors the ScopeView
// dashboard layout (sticky header banner + 4 KPI cards + pipeline
// distribution card + projects overview). Used when switching
// between hierarchy nodes (CM/TL/Dept) in the sidebar so the
// entire dashboard area is covered — not just a small centered box.
// ─────────────────────────────────────────────────────────────
export function ScopeViewSkeleton() {
  return (
    <SkeletonShimmerWrap
      className="flex-1 overflow-hidden flex flex-col"
      data-testid="scope-view-skeleton"
    >
      {/* Sticky header banner — matches ScopeView's real header */}
      <div className="sticky top-0 z-20 bg-white border-b border-slate-200">
        <div className="px-8 py-5">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4 min-w-0">
              <Skeleton className="h-14 w-14 rounded-xl shrink-0" />
              <div className="min-w-0 space-y-2">
                <Skeleton className="h-3 w-28" />
                <Skeleton className="h-8 w-64" />
                <Skeleton className="h-3.5 w-48" />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Skeleton className="h-8 w-28 rounded-md" />
              <Skeleton className="h-8 w-36 rounded-lg" />
            </div>
          </div>
        </div>
      </div>

      {/* Body: KPIs + Pipeline + Projects overview */}
      <div className="flex-1 overflow-y-auto scrollbar-thin">
        <div className="px-8 py-6 space-y-6">
          {/* 4 KPI cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="rounded-lg border border-slate-200 bg-white p-4 space-y-3"
              >
                <div className="flex items-center justify-between">
                  <Skeleton className="h-3 w-20" />
                  <Skeleton className="h-8 w-8 rounded-lg" />
                </div>
                <Skeleton className="h-8 w-16" />
                <Skeleton className="h-2.5 w-24" />
              </div>
            ))}
          </div>

          {/* Pipeline distribution card */}
          <div className="rounded-lg border border-slate-200 bg-white p-5 space-y-4">
            <div className="flex items-center justify-between">
              <Skeleton className="h-4 w-44" />
              <Skeleton className="h-3 w-56" />
            </div>
            <Skeleton className="h-3 w-full rounded-full" />
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div
                  key={i}
                  className="p-3 rounded-lg border border-slate-100 bg-slate-50 space-y-2"
                >
                  <div className="flex items-center gap-1.5">
                    <Skeleton className="h-3 w-3 rounded" />
                    <Skeleton className="h-2.5 w-16" />
                  </div>
                  <Skeleton className="h-6 w-10" />
                </div>
              ))}
            </div>
          </div>

          {/* Projects overview card — three mini project rows */}
          <div className="rounded-lg border border-slate-200 bg-white p-5 space-y-4">
            <div className="flex items-center justify-between">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-3 w-20" />
            </div>
            {Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="rounded-lg border border-slate-200 p-4 bg-slate-50/40 space-y-3"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <Skeleton className="h-4 w-4 rounded shrink-0" />
                    <Skeleton className="h-3.5 w-48" />
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <Skeleton className="h-3 w-16" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                </div>
                <Skeleton className="h-2.5 w-full rounded-full" />
                <div className="grid grid-cols-5 gap-2">
                  {Array.from({ length: 5 }).map((_, j) => (
                    <div
                      key={j}
                      className="p-2 rounded-md border border-slate-100 bg-white space-y-1.5"
                    >
                      <div className="flex items-center gap-1">
                        <Skeleton className="h-3 w-3 rounded" />
                        <Skeleton className="h-2 w-12" />
                      </div>
                      <Skeleton className="h-4 w-8" />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </SkeletonShimmerWrap>
  );
}

// ─────────────────────────────────────────────────────────────
// ProjectViewSkeleton — full-width loader that mirrors the project
// spreadsheet (sticky header with project title + view toggle,
// pipeline bar, and a virtualized-looking table body).
// ─────────────────────────────────────────────────────────────
export function ProjectViewSkeleton() {
  const columnCount = 9;
  return (
    <SkeletonShimmerWrap
      className="flex-1 min-w-0 flex flex-col overflow-hidden"
      data-testid="project-view-skeleton"
    >
      {/* Sticky project header — mirrors ProjectView's real header */}
      <div className="sticky top-0 z-20 bg-white border-b border-slate-200">
        <div className="px-6 py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 space-y-2">
              <Skeleton className="h-3 w-16" />
              <Skeleton className="h-8 w-72" />
              <Skeleton className="h-3 w-56" />
            </div>
            <div className="flex items-center gap-3">
              {/* Collaborator avatar stack */}
              <div className="flex items-center -space-x-1.5">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton
                    key={i}
                    className="h-7 w-7 rounded-full ring-2 ring-white"
                  />
                ))}
              </div>
              {/* View toggle group */}
              <Skeleton className="h-8 w-52 rounded-lg" />
              {/* Add column button */}
              <Skeleton className="h-8 w-28 rounded-md" />
            </div>
          </div>
        </div>
      </div>

      {/* Pipeline bar */}
      <div className="border-b border-slate-200 bg-white">
        <div className="px-6 py-3 space-y-2.5">
          <div className="flex items-center gap-3">
            <Skeleton className="h-2.5 w-16" />
            <Skeleton className="h-2 flex-1 rounded-full" />
            <Skeleton className="h-2.5 w-14" />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton
                key={i}
                className={`h-6 ${i === 0 ? "w-24" : "w-28"} rounded-md`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Search + filter row */}
      <div className="border-b border-slate-200 bg-white px-6 py-2.5">
        <div className="flex items-center gap-2">
          <Skeleton className="h-8 w-56 rounded-md" />
          <Skeleton className="h-8 w-32 rounded-md" />
          <Skeleton className="h-8 w-32 rounded-md" />
          <Skeleton className="h-8 w-24 rounded-md" />
          <div className="flex-1" />
          <Skeleton className="h-8 w-8 rounded-md" />
          <Skeleton className="h-8 w-24 rounded-md" />
        </div>
      </div>

      {/* Spreadsheet body: sticky header row + many skeleton rows */}
      <div className="flex-1 overflow-hidden bg-white">
        {/* Sheet header */}
        <div className="border-b-2 border-slate-200 bg-slate-50 px-6 py-2.5">
          <div className="flex items-center gap-3">
            <Skeleton className="h-4 w-4 rounded-sm shrink-0" />
            {Array.from({ length: columnCount }).map((_, i) => (
              <Skeleton
                key={i}
                className={`h-3 ${
                  i === 0
                    ? "w-24"
                    : i === 1
                    ? "flex-1 max-w-[220px]"
                    : i === 8
                    ? "w-16"
                    : "w-24"
                }`}
              />
            ))}
          </div>
        </div>
        {/* Sheet body rows */}
        <div className="divide-y divide-slate-100">
          {Array.from({ length: 14 }).map((_, r) => (
            <div key={r} className="px-6 py-2.5">
              <div className="flex items-center gap-3">
                <Skeleton className="h-4 w-4 rounded-sm shrink-0" />
                {Array.from({ length: columnCount }).map((_, c) => (
                  <Skeleton
                    key={c}
                    className={`h-3.5 ${
                      c === 0
                        ? "w-24"
                        : c === 1
                        ? "flex-1 max-w-[220px]"
                        : c === 5 || c === 6
                        ? "w-24 rounded-full"
                        : c === 7
                        ? "w-28 rounded-full"
                        : c === 8
                        ? "w-16"
                        : "w-24"
                    }`}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </SkeletonShimmerWrap>
  );
}

// ─────────────────────────────────────────────────────────────
// CalendarGridSkeleton — 7-col grid for month calendars (attendance).
// ─────────────────────────────────────────────────────────────
export function CalendarGridSkeleton({ rows = 5 }) {
  return (
    <div
      className="grid grid-cols-7 gap-2"
      data-testid="calendar-grid-skeleton"
    >
      {Array.from({ length: rows * 7 }).map((_, i) => (
        <Skeleton key={i} className="h-20 w-full rounded-md" />
      ))}
    </div>
  );
}
